/**
 * NYMPHÉ — Minimal analytics helper
 * Fans out a single `track(name, props)` call to GA4 + Plausible.
 *
 * Config (frontend/.env):
 *   REACT_APP_GA4_MEASUREMENT_ID   e.g. G-XXXXXXXXXX
 *   REACT_APP_PLAUSIBLE_DOMAIN     e.g. nymphe.co
 *
 * Behaviour:
 *   - If either env var is missing, that platform silently no-ops.
 *   - Zero PII. Only event names + non-identifying props.
 *   - One-time script injection guarded by window flags.
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

let initialised = false;

const loadScript = (src, attrs = {}) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
};

export const initAnalytics = () => {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  // ── GA4 ──────────────────────────────────────────────
  if (GA4_ID) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { send_page_view: true });
  }

  // ── Plausible ────────────────────────────────────────
  if (PLAUSIBLE_DOMAIN) {
    loadScript('https://plausible.io/js/script.tagged-events.js', {
      'data-domain': PLAUSIBLE_DOMAIN,
    });
    window.plausible = window.plausible || function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
  }
};

/**
 * Fire a single event to all configured platforms.
 * @param {string} name  snake_case event name
 * @param {object} [props]  non-identifying metadata only
 */
export const track = (name, props = {}) => {
  if (typeof window === 'undefined' || !name) return;
  try {
    if (GA4_ID && typeof window.gtag === 'function') {
      window.gtag('event', name, props);
    }
    if (PLAUSIBLE_DOMAIN && typeof window.plausible === 'function') {
      window.plausible(name, Object.keys(props).length ? { props } : undefined);
    }
    // First-party beacon — server-side funnel panel in /admin
    if (API_BASE && typeof fetch === 'function') {
      const body = JSON.stringify({ name, props: props || {} });
      if (navigator.sendBeacon) {
        // sendBeacon guarantees delivery even on page unload
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE}/api/events`, blob);
      } else {
        fetch(`${API_BASE}/api/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    }
  } catch {
    /* analytics must never break the UI */
  }
};
